import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useGuestGate } from "../lib/useGuestGate";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Trash2,
  Pencil,
  TrendingUp,
  TrendingDown,
  Waves,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { ticker: "", industry: "", trend: "uptrend", wave_counter: "1", comment: "" };

const WAVE_OPTIONS = ["1", "2", "3", "4", "5a", "5b", "5c", "5d", "5e"];

function TrendToggle({ value, onChange, testid }) {
  const isUp = value === "uptrend";
  return (
    <div
      className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5"
      role="group"
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => onChange("uptrend")}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold tracking-wide transition-colors ${
          isUp
            ? "bg-white text-emerald-700 shadow-sm"
            : "text-slate-500 hover:text-slate-700"
        }`}
        data-testid={`${testid}-uptrend`}
        aria-pressed={isUp}
      >
        <TrendingUp className="w-3.5 h-3.5" strokeWidth={2.25} />
        Uptrend
      </button>
      <button
        type="button"
        onClick={() => onChange("downtrend")}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold tracking-wide transition-colors ${
          !isUp
            ? "bg-white text-red-600 shadow-sm"
            : "text-slate-500 hover:text-slate-700"
        }`}
        data-testid={`${testid}-downtrend`}
        aria-pressed={!isUp}
      >
        <TrendingDown className="w-3.5 h-3.5" strokeWidth={2.25} />
        Downtrend
      </button>
    </div>
  );
}

function TrendBadge({ trend }) {
  const isUp = trend === "uptrend";
  const Icon = isUp ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border ${
        isUp
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-red-50 text-red-600 border-red-200"
      }`}
    >
      <Icon className="w-3 h-3" strokeWidth={2} />
      {isUp ? "Uptrend" : "Downtrend"}
    </span>
  );
}

export default function WatchlistPage() {
  const { gate } = useGuestGate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [mode, setMode] = useState(null); // "create" | "edit" | null
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/watchlist");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load watchlist");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    if (gate("Adding to the watchlist")) return;
    setEditingId(null);
    setForm(EMPTY_FORM);
    setMode("create");
  };

  const openEdit = (it) => {
    if (gate("Editing a watchlist item")) return;
    setEditingId(it.id);
    setForm({
      ticker: it.ticker,
      industry: it.industry || "",
      trend: it.trend,
      wave_counter: String(it.wave_counter),
      comment: it.comment || "",
    });
    setMode("edit");
  };

  const closeDialog = () => {
    setMode(null);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ticker.trim()) return;
    setSubmitting(true);
    const body = {
      ticker: form.ticker.trim().toUpperCase(),
      industry: form.industry.trim(),
      trend: form.trend,
      wave_counter: form.wave_counter,
      comment: form.comment,
    };
    try {
      if (mode === "edit" && editingId) {
        const { data } = await api.put(`/watchlist/${editingId}`, body);
        setItems((prev) => prev.map((it) => (it.id === editingId ? data : it)));
        toast.success(`${data.ticker} updated`);
      } else {
        const { data } = await api.post("/watchlist", body);
        setItems((prev) => [data, ...prev]);
        toast.success(`${data.ticker} added to watchlist`);
      }
      closeDialog();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          (mode === "edit" ? "Failed to update item" : "Failed to add item")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, ticker) => {
    if (gate("Removing a watchlist item")) return;
    try {
      await api.delete(`/watchlist/${id}`);
      setItems((prev) => prev.filter((s) => s.id !== id));
      toast.success(`${ticker} removed from watchlist`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove item");
    } finally {
      setConfirmTarget(null);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-8" data-testid="watchlist-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <p className="text-slate-600 text-sm leading-relaxed max-w-xl">
          Tickers your team is actively monitoring. Track trend direction, wave
          count and any notes per name.
        </p>
        <Button
          onClick={openCreate}
          className="bg-slate-900 hover:bg-slate-800 text-white gap-2"
          data-testid="add-watchlist-button"
        >
          <Plus className="w-4 h-4" />
          Add to Watchlist
        </Button>
      </div>

      <Dialog open={mode !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="sm:max-w-md" data-testid="watchlist-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {mode === "edit" ? "Edit Watchlist Item" : "Add to Watchlist"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label
                htmlFor="wl-ticker"
                className="text-xs tracking-[0.15em] uppercase text-slate-600"
              >
                Ticker
              </Label>
              <Input
                id="wl-ticker"
                required
                value={form.ticker}
                onChange={(e) =>
                  setForm((f) => ({ ...f, ticker: e.target.value.toUpperCase() }))
                }
                placeholder="AAPL"
                maxLength={10}
                className="mt-1.5 font-mono uppercase"
                data-testid="wl-ticker-input"
              />
            </div>

            <div>
              <Label
                htmlFor="wl-industry"
                className="text-xs tracking-[0.15em] uppercase text-slate-600"
              >
                Industry
              </Label>
              <Input
                id="wl-industry"
                value={form.industry}
                onChange={set("industry")}
                placeholder="Consumer Electronics"
                className="mt-1.5"
                data-testid="wl-industry-input"
              />
            </div>

            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-slate-600">
                Trend
              </Label>
              <div className="mt-1.5">
                <TrendToggle
                  value={form.trend}
                  onChange={(v) => setForm((f) => ({ ...f, trend: v }))}
                  testid="wl-trend"
                />
              </div>
            </div>

            <div>
              <Label
                htmlFor="wl-wave"
                className="text-xs tracking-[0.15em] uppercase text-slate-600"
              >
                Wave Counter
              </Label>
              <Select
                value={form.wave_counter}
                onValueChange={(v) => setForm((f) => ({ ...f, wave_counter: v }))}
              >
                <SelectTrigger
                  id="wl-wave"
                  className="mt-1.5 font-mono tabular-nums"
                  data-testid="wl-wave-input"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WAVE_OPTIONS.map((w) => (
                    <SelectItem
                      key={w}
                      value={w}
                      className="font-mono tabular-nums"
                      data-testid={`wl-wave-option-${w}`}
                    >
                      {w}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label
                htmlFor="wl-comment"
                className="text-xs tracking-[0.15em] uppercase text-slate-600"
              >
                Comment
              </Label>
              <Textarea
                id="wl-comment"
                value={form.comment}
                onChange={set("comment")}
                placeholder="Setup, levels, catalysts..."
                rows={3}
                className="mt-1.5"
                data-testid="wl-comment-input"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeDialog}
                data-testid="cancel-watchlist-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="submit-watchlist-button"
              >
                {submitting
                  ? "Saving..."
                  : mode === "edit"
                  ? "Save Changes"
                  : "Add to Watchlist"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div
        className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm"
        data-testid="watchlist-table"
      >
        <table className="w-full min-w-[640px] table-auto">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Ticker
              </th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">
                Industry
              </th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Trend
              </th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Wave
              </th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Comment
              </th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[120px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-slate-500">
                  Loading watchlist...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-16 text-center" data-testid="empty-watchlist">
                  <div className="text-sm text-slate-500">No tickers on the watchlist yet.</div>
                  <div className="text-xs text-slate-400 mt-1">
                    Click "Add to Watchlist" to start tracking one.
                  </div>
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr
                  key={it.id}
                  className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                  data-testid={`watchlist-row-${it.ticker}`}
                >
                  <td className="py-4 px-6">
                    <span className="font-mono font-semibold text-slate-900 text-base tracking-wide">
                      {it.ticker}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-sm text-slate-700 hidden md:table-cell">
                    {it.industry ? (
                      it.industry
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <TrendBadge trend={it.trend} />
                  </td>
                  <td className="py-4 px-6">
                    <span className="inline-flex items-center gap-1.5 font-mono tabular-nums text-sm text-slate-700">
                      <Waves className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.75} />
                      {it.wave_counter}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-sm text-slate-700 max-w-md">
                    {it.comment ? (
                      <span className="line-clamp-2">{it.comment}</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="py-4 px-6 text-right w-[120px]">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => openEdit(it)}
                        aria-label={`Edit ${it.ticker}`}
                        title={`Edit ${it.ticker}`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition-colors"
                        data-testid={`edit-watchlist-${it.id}`}
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        onClick={() => { if (gate("Removing a watchlist item")) return; setConfirmTarget({ id: it.id, ticker: it.ticker }); }}
                        aria-label={`Remove ${it.ticker}`}
                        title={`Remove ${it.ticker}`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                        data-testid={`delete-watchlist-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog
        open={confirmTarget !== null}
        onOpenChange={(o) => !o && setConfirmTarget(null)}
      >
        <AlertDialogContent data-testid="delete-watchlist-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from watchlist?</AlertDialogTitle>
            <AlertDialogDescription>
              Stop tracking{" "}
              <span className="font-mono text-slate-800">
                {confirmTarget?.ticker}
              </span>
              ? You can always add it back later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-watchlist">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmTarget && handleDelete(confirmTarget.id, confirmTarget.ticker)
              }
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-watchlist"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
